// frontend/src/hooks/useLocalRecording.js
//
// React-brygga för lokal-först-inspelning. Skapar en lokal session (IndexedDB)
// med ett klientgenererat id, köar chunkar lokalt och exponerar vänteläge för
// UI:t. Bekräftad transkript-text kommer via uploader-event → onConfirmedText.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  putSession,
  setServerSessionId,
  markSessionFinished,
  updateSessionMeta,
  putChunk,
  getPendingCountsBySession,
} from '../services/localRecording/db';
import {
  startUploader,
  wakeUploader,
  subscribeUploader,
} from '../services/localRecording/uploader';

function newClientId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `cs-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function useLocalRecording({ onConfirmedText, onServerSession, onSessionCompleted } = {}) {
  const clientSessionIdRef = useRef(null);
  const chunkIndexRef = useRef(0);
  const [pendingCounts, setPendingCounts] = useState({});

  // Callbacks i ref så uploader-prenumerationen inte behöver sättas om.
  const cbRef = useRef({});
  cbRef.current = { onConfirmedText, onServerSession, onSessionCompleted };

  const refreshPending = useCallback(async () => {
    try {
      setPendingCounts(await getPendingCountsBySession());
    } catch (_) { /* IndexedDB kan saknas i vissa lägen */ }
  }, []);

  // Montera uppladdaren globalt (idempotent) och lyssna på dess event.
  useEffect(() => {
    startUploader();
    refreshPending();
    const unsub = subscribeUploader((ev) => {
      if (ev.type === 'confirmed' && ev.text) cbRef.current.onConfirmedText?.(ev);
      if (ev.type === 'session_completed') cbRef.current.onSessionCompleted?.(ev);
      if (ev.type === 'pending_changed') refreshPending();
    });
    return unsub;
  }, [refreshPending]);

  // Starta en ny lokal session. Returnerar client_session_id direkt (ingen
  // server-round-trip krävs — inspelningen kan börja även om servern är nere).
  const beginSession = useCallback(async ({ templateId, sessionName } = {}) => {
    const clientSessionId = newClientId();
    clientSessionIdRef.current = clientSessionId;
    chunkIndexRef.current = 0;
    await putSession({
      client_session_id: clientSessionId,
      server_session_id: null,
      template_id: templateId ?? null,
      session_name: sessionName ?? null,
      created_at: Date.now(),
      finished_at: null,
      status: 'recording',
    });
    await refreshPending();
    return clientSessionId;
  }, [refreshPending]);

  // Koppla ihop med server-session (när createSession lyckades vid start).
  const attachServerSession = useCallback(async (serverSessionId) => {
    const cs = clientSessionIdRef.current;
    if (cs && serverSessionId) {
      await setServerSessionId(cs, serverSessionId);
      cbRef.current.onServerSession?.({ clientSessionId: cs, serverSessionId });
    }
  }, []);

  // Köa en chunk lokalt (skrivs till IndexedDB FÖRST) och väck uppladdaren.
  const enqueueChunk = useCallback(async (blob, isFinal = false) => {
    const cs = clientSessionIdRef.current;
    if (!cs || !blob) return;
    const chunkIndex = chunkIndexRef.current++;
    await putChunk({ clientSessionId: cs, chunkIndex, blob, isFinal });
    wakeUploader();
    refreshPending();
  }, [refreshPending]);

  // Uppdatera mall/namn på aktiv lokal session (t.ex. vald efter start).
  const setSessionMeta = useCallback(async ({ templateId, sessionName } = {}) => {
    const cs = clientSessionIdRef.current;
    if (!cs) return;
    await updateSessionMeta(cs, { templateId, sessionName });
  }, []);

  // Markera sessionen klar lokalt. Server-complete sker när kön tömts.
  const finishSession = useCallback(async () => {
    const cs = clientSessionIdRef.current;
    if (!cs) return;
    await markSessionFinished(cs);
    wakeUploader();
  }, []);

  return {
    beginSession,
    attachServerSession,
    enqueueChunk,
    finishSession,
    setSessionMeta,
    refreshPending,
    pendingCounts,
    getClientSessionId: () => clientSessionIdRef.current,
  };
}
